import _extends from "@babel/runtime/helpers/extends";
import React from 'react';
import defaultClasses from './image.css';
import { arrayOf, bool, oneOf, shape, string } from 'prop-types';
import { Link, resourceUrl } from '@magento/venia-drivers';
import resolveLinkProps from '../../resolveLinkProps';
import { mergeClasses } from '@magento/venia-ui/lib/classify';

/**
 * Page Builder Image component.
 *
 * This component is part of the Page Builder / PWA integration. It can be consumed without Page Builder.
 *
 * @typedef Image
 * @kind functional component
 *
 * @param {props} props React component props
 *
 * @returns {React.Element} A React component that displays an Image.
 */
var Image = props => {
  var classes = mergeClasses(defaultClasses, props.classes);
  var {
    desktopImage,
    mobileImage,
    altText,
    title,
    link,
    openInNewTab,
    caption,
    textAlign,
    border,
    borderColor,
    borderWidth,
    borderRadius,
    marginTop,
    marginRight,
    marginBottom,
    marginLeft,
    paddingTop,
    paddingRight,
    paddingBottom,
    paddingLeft,
    cssClasses = []
  } = props;
  var figureStyles = {
    textAlign,
    marginTop,
    marginRight,
    marginBottom,
    marginLeft,
    paddingTop,
    paddingRight,
    paddingBottom,
    paddingLeft
  };
  var imageStyles = {
    border,
    borderColor,
    borderWidth,
    borderRadius
  };

  // Don't render anything if there is no image to be rendered
  if (!desktopImage && !mobileImage) {
    return null;
  }
  var SourceFragment = mobileImage ? /*#__PURE__*/React.createElement("source", {
    media: "(max-width: 768px)",
    srcSet: resourceUrl(mobileImage, {
      type: 'image-wysiwyg',
      quality: 85
    })
  }) : '';
  var PictureFragment = /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("picture", null, SourceFragment, /*#__PURE__*/React.createElement("img", {
    className: classes.img,
    src: resourceUrl(desktopImage, {
      type: 'image-wysiwyg',
      quality: 85
    }),
    title: title,
    alt: altText,
    style: imageStyles,
    loading: "lazy"
  })), caption ? /*#__PURE__*/React.createElement("figcaption", null, caption) : '');
  if (typeof link === 'string') {
    var linkProps = resolveLinkProps(link);
    var LinkComponent = linkProps.to ? Link : 'a';
    return /*#__PURE__*/React.createElement("figure", {
      style: figureStyles,
      className: cssClasses.join(' ')
    }, /*#__PURE__*/React.createElement(LinkComponent, _extends({}, linkProps, openInNewTab ? {
      target: '_blank'
    } : ''), PictureFragment));
  } else {
    return /*#__PURE__*/React.createElement("figure", {
      style: figureStyles,
      className: cssClasses.join(' ')
    }, PictureFragment);
  }
};

/**
 * Props for {@link Image}
 *
 * @typedef props
 *
 * @property {Object} classes An object containing the class names for the Image
 * @property {String} classes.img CSS classes for the img element
 * @property {String} desktopImage URL src of the desktop image
 * @property {String} mobileImage URL src of the mobile image
 * @property {String} altText Alternate text
 * @property {String} title Title of the image
 * @property {String} link URL to redirect to
 * @property {String} linkType Type of link
 * @property {bool} openInNewTab Flag to indicate if link should be opened in a new tab
 * @property {String} caption Caption for the image
 * @property {String} textAlign Alignment of the divider within the parent container
 * @property {String} border CSS border property
 * @property {String} borderColor CSS border color property
 * @property {String} borderWidth CSS border width property
 * @property {String} borderRadius CSS border radius property
 * @property {String} marginTop CSS margin top property
 * @property {String} marginRight CSS margin right property
 * @property {String} marginBottom CSS margin bottom property
 * @property {String} marginLeft CSS margin left property
 * @property {String} paddingTop CSS padding top property
 * @property {String} paddingRight CSS padding right property
 * @property {String} paddingBottom CSS padding bottom property
 * @property {String} paddingLeft CSS padding left property
 * @property {Array} cssClasses List of CSS classes to be applied to the component
 */
Image.propTypes = {
  classes: shape({
    img: string
  }),
  desktopImage: string,
  mobileImage: string,
  altText: string,
  title: string,
  link: string,
  linkType: oneOf(['default', 'category', 'product', 'page']),
  openInNewTab: bool,
  caption: string,
  textAlign: string,
  border: string,
  borderColor: string,
  borderWidth: string,
  borderRadius: string,
  marginTop: string,
  marginRight: string,
  marginBottom: string,
  marginLeft: string,
  paddingTop: string,
  paddingRight: string,
  paddingBottom: string,
  cssClasses: arrayOf(string)
};
export default Image;